import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { useEffect, useRef, useSyncExternalStore } from 'react';

import { getAuthToken } from './client';

const SUBSCRIPTION_REGEX = /subscription\s+(\w+)\s*\(([^)]*)\)\s*{([\s\S]*?)}/i;

const getSubscriptionNameFromQuery = (subscriptionQuery: string) => {
  const match = subscriptionQuery.match(SUBSCRIPTION_REGEX);
  if (!match) return null;
  return match[1][0].toLowerCase() + match[1].slice(1);
};

const getRealtimeUrl = (httpEndpoint: string): string => {
  return httpEndpoint.replace('https://', 'wss://').replace('appsync-api', 'appsync-realtime-api');
};

let subscriptionEndpoint =
  typeof window !== 'undefined'
    ? ((import.meta as unknown as Record<string, Record<string, string>>).env?.VITE_GRAPHQL_ENDPOINT ?? '')
    : (process.env.VITE_GRAPHQL_ENDPOINT ?? '');

export const setSubscriptionEndpoint = (endpoint: string): void => {
  subscriptionEndpoint = endpoint;
};

interface SubscriptionMessage {
  id?: string;
  type: string;
  payload?: Record<string, unknown>;
}

interface Subscriber {
  id: string;
  query: string;
  onData: (data: unknown) => void;
  onError?: (error: Error) => void;
  started: boolean;
}

const subscribers = new Map<string, Subscriber>();
const reconnectListeners = new Set<() => void>();
const connectionListeners = new Set<() => void>();
let hasAcknowledged = false;
let ws: WebSocket | null = null;
let connecting = false;
let connected = false;
let keepAliveTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
let reconnectAttempt = 0;
let connectAttemptId = 0;

const setConnected = (isConnected: boolean) => {
  if (connected === isConnected) {
    return;
  }
  connected = isConnected;
  connectionListeners.forEach((listener) => listener());
};

const resetKeepAlive = () => {
  if (keepAliveTimer) clearTimeout(keepAliveTimer);
  keepAliveTimer = setTimeout(
    () => {
      console.warn('AppSync keep-alive timeout, reconnecting...');
      ws?.close();
    },
    5 * 60 * 1000
  );
};

// The socket outlives the 15-minute JWT, so a subscription started later needs a token of its own
const sendStart = async (sub: Subscriber) => {
  const socket = ws;
  if (isNil(socket) || socket.readyState !== WebSocket.OPEN) {
    return;
  }
  const token = await getAuthToken();
  const isStillWanted = ws === socket && socket.readyState === WebSocket.OPEN && subscribers.has(sub.id);
  if (!isStillWanted || sub.started) {
    return;
  }
  if (isNil(token)) {
    socket.close();
    return;
  }
  socket.send(
    JSON.stringify({
      id: sub.id,
      type: 'start',
      payload: {
        data: JSON.stringify({ query: sub.query }),
        extensions: {
          authorization: {
            Authorization: `Bearer ${token}`,
            host: new URL(subscriptionEndpoint).host,
          },
        },
      },
    })
  );
  sub.started = true;
};

const listUnstartedSubscribers = (): Subscriber[] => Array.from(subscribers.values()).filter((sub) => !sub.started);

// Consumers drop their polling fallback and refetch on reconnection once connected: every start must be sent first,
// including those of subscribers added while earlier starts waited for their token
const publishConnection = async (socket: WebSocket) => {
  subscribers.forEach((sub) => {
    sub.started = false;
  });
  let unstartedSubscribers = listUnstartedSubscribers();
  while (!isEmpty(unstartedSubscribers)) {
    await Promise.all(unstartedSubscribers.map(sendStart));
    if (ws !== socket || socket.readyState !== WebSocket.OPEN) {
      return;
    }
    unstartedSubscribers = listUnstartedSubscribers();
  }
  if (ws !== socket || socket.readyState !== WebSocket.OPEN) {
    return;
  }
  connecting = false;
  setConnected(true);
  if (hasAcknowledged) {
    reconnectListeners.forEach((listener) => listener());
  }
  hasAcknowledged = true;
};

const scheduleReconnect = () => {
  if (reconnectTimer) return;
  if (subscribers.size === 0) return;
  const delay = Math.min(60_000, 2_000 * 2 ** reconnectAttempt);
  reconnectAttempt += 1;
  reconnectTimer = setTimeout(() => {
    reconnectTimer = null;
    connect();
  }, delay);
};

const connect = async () => {
  if (connecting || connected) return;
  if (subscribers.size === 0) return;
  connecting = true;
  connectAttemptId += 1;
  const attemptId = connectAttemptId;

  const token = await getAuthToken();
  if (attemptId !== connectAttemptId) {
    return;
  }
  if (!token) {
    connecting = false;
    scheduleReconnect();
    return;
  }

  const realtimeUrl = getRealtimeUrl(subscriptionEndpoint);
  const header = btoa(
    JSON.stringify({
      Authorization: `Bearer ${token}`,
      host: new URL(subscriptionEndpoint).host,
    })
  );
  const payload = btoa(JSON.stringify({}));
  const url = `${realtimeUrl}?header=${encodeURIComponent(header)}&payload=${encodeURIComponent(payload)}`;

  const socket = new WebSocket(url, ['graphql-ws']);
  ws = socket;

  socket.onopen = () => {
    if (ws !== socket) {
      socket.close();
      return;
    }
    socket.send(JSON.stringify({ type: 'connection_init' }));
  };

  socket.onmessage = (event) => {
    if (ws !== socket) {
      return;
    }
    const message: SubscriptionMessage = JSON.parse(event.data);

    switch (message.type) {
      case 'connection_ack': {
        reconnectAttempt = 0;
        resetKeepAlive();
        void publishConnection(socket);
        break;
      }
      case 'ka':
        resetKeepAlive();
        break;
      case 'data': {
        const sub = message.id ? subscribers.get(message.id) : undefined;
        if (sub && message.payload?.data) sub.onData(message.payload.data);
        break;
      }
      case 'error': {
        const sub = message.id ? subscribers.get(message.id) : undefined;
        const subscriptionError = new Error(JSON.stringify(message.payload));
        if (!sub) {
          console.error('AppSync connection-level error:', message.payload);
          break;
        }
        console.error(`AppSync subscription rejected: ${getSubscriptionNameFromQuery(sub.query)}`, message.payload);
        sub.onError?.(subscriptionError);
        break;
      }
      case 'complete':
        break;
    }
  };

  socket.onerror = (event) => {
    if (ws !== socket) {
      return;
    }
    console.warn('AppSync WebSocket error, reconnecting...', event);
  };

  socket.onclose = () => {
    if (ws !== socket) {
      return;
    }
    setConnected(false);
    connecting = false;
    if (keepAliveTimer) {
      clearTimeout(keepAliveTimer);
      keepAliveTimer = null;
    }
    ws = null;
    for (const sub of subscribers.values()) sub.started = false;
    scheduleReconnect();
  };
};

export const reconnectSubscriptions = (): void => {
  ws?.close();
};

/** Called each time the socket is acknowledged again after a drop: the events sent meanwhile were missed. */
export const onSubscriptionsReconnected = (listener: () => void): (() => void) => {
  reconnectListeners.add(listener);
  return () => {
    reconnectListeners.delete(listener);
  };
};

/** Whether the socket is acknowledged, so the events of the subscriptions reach this tab. */
export const isSubscriptionsConnected = (): boolean => connected;

export const onSubscriptionsConnectionChange = (listener: () => void): (() => void) => {
  connectionListeners.add(listener);
  return () => {
    connectionListeners.delete(listener);
  };
};

export const useSubscriptionsConnected = (): boolean =>
  useSyncExternalStore(onSubscriptionsConnectionChange, isSubscriptionsConnected, () => false);

const subscribe = (query: string, onData: (data: unknown) => void, onError?: (error: Error) => void): (() => void) => {
  const id = crypto.randomUUID();
  const sub: Subscriber = { id, query, onData, onError, started: false };
  subscribers.set(id, sub);

  if (connected) void sendStart(sub);
  else if (!connecting) connect();

  return () => {
    const existing = subscribers.get(id);
    if (!existing) return;
    subscribers.delete(id);
    if (existing.started && ws?.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ id, type: 'stop' }));
    }
    if (subscribers.size === 0) {
      if (reconnectTimer) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      if (keepAliveTimer) {
        clearTimeout(keepAliveTimer);
        keepAliveTimer = null;
      }
      connectAttemptId += 1;
      ws?.close();
      ws = null;
      setConnected(false);
      connecting = false;
      reconnectAttempt = 0;
      hasAcknowledged = false;
    }
  };
};

export const useSubscriptions = <C extends object, U extends object, D extends object>(
  onCreateSubscriptionQuery?: string,
  onUpdateSubscriptionQuery?: string,
  onDeleteSubscriptionQuery?: string,
  onCreateData?: (data: C) => void,
  onUpdateData?: (data: U) => void,
  onDeleteData?: (data: D) => void,
  onCreateDataError?: (error: Error) => void,
  onUpdateDataError?: (error: Error) => void,
  onDeleteDataError?: (error: Error) => void,
  _lambdaAuthMode?: boolean
) => {
  if (!onCreateSubscriptionQuery && !onUpdateSubscriptionQuery && !onDeleteSubscriptionQuery) {
    throw new Error('useSubscriptions: No subscription queries provided');
  }

  const unsubscribesRef = useRef<(() => void)[]>([]);

  useEffect(() => {
    const unsubscribes: (() => void)[] = [];

    if (onCreateSubscriptionQuery && onCreateData) {
      const createName = getSubscriptionNameFromQuery(onCreateSubscriptionQuery);
      unsubscribes.push(
        subscribe(
          onCreateSubscriptionQuery,
          (data) => {
            const d = data as Record<string, C>;
            if (createName && d[createName]) {
              onCreateData({ [createName]: d[createName] } as unknown as C);
            }
          },
          onCreateDataError
        )
      );
    }

    if (onUpdateSubscriptionQuery && onUpdateData) {
      const updateName = getSubscriptionNameFromQuery(onUpdateSubscriptionQuery);
      unsubscribes.push(
        subscribe(
          onUpdateSubscriptionQuery,
          (data) => {
            const d = data as Record<string, U>;
            if (updateName && d[updateName]) {
              onUpdateData({ [updateName]: d[updateName] } as unknown as U);
            }
          },
          onUpdateDataError
        )
      );
    }

    if (onDeleteSubscriptionQuery && onDeleteData) {
      const deleteName = getSubscriptionNameFromQuery(onDeleteSubscriptionQuery);
      unsubscribes.push(
        subscribe(
          onDeleteSubscriptionQuery,
          (data) => {
            const d = data as Record<string, D>;
            if (deleteName && d[deleteName]) {
              onDeleteData({ [deleteName]: d[deleteName] } as unknown as D);
            }
          },
          onDeleteDataError
        )
      );
    }

    unsubscribesRef.current = unsubscribes;

    return () => {
      unsubscribes.forEach((unsub) => unsub());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
};
